import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { PreVisitContactType } from '@hb/shared'
import { useField, useForm } from 'react-final-form'
import { SectionLabel } from '../../SectionLabel'

const contactTypeLabels: Record<PreVisitContactType, string> = {
  'phone-call': 'Phone call',
  'video-call': 'Video call',
  'in-person-only': 'In-person only (no pre-visit contact)',
}

// §2.16: Pre-visit contact section — optional. Captures a phone/video call that occurred
// before the in-person encounter on the same date of service. Time auto-populates into
// the Time & Work Log when contactMinutes is filled.
export const PreVisitContactInput = () => {
  const form = useForm()
  const {
    input: { value: contactType },
  } = useField<PreVisitContactType | undefined>('data.preVisitContact.contactType', {
    subscription: { value: true },
  })
  const {
    input: { value: contactMinutes },
  } = useField<number | undefined>('data.timeWorkLog.preVisitContactMinutes', {
    subscription: { value: true },
  })
  const {
    input: { value: contactNote },
  } = useField<string | undefined>('data.preVisitContact.contactNote', {
    subscription: { value: true },
  })

  const minutesInvalid = !!contactType && contactType !== 'in-person-only' && !contactMinutes

  return (
    <Box data-child-name="data.preVisitContact" px={4} py={3}>
      <SectionLabel>Pre-Visit Contact</SectionLabel>
      <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.400' }} mb={3}>
        If a phone or video call occurred before this in-person visit on the same date, document it
        here. The time will be included in the total visit time.
      </Text>
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs" fontWeight={600} fontFamily="Hero-New" mb={1}>
            Contact type
          </FormLabel>
          <RadioGroup
            value={contactType ?? ''}
            onChange={val =>
              form.change(
                'data.preVisitContact.contactType',
                val as PreVisitContactType | undefined,
              )
            }>
            <VStack align="flex-start" spacing={1}>
              {(Object.keys(contactTypeLabels) as PreVisitContactType[]).map(k => (
                <Radio key={k} value={k} size="sm" fontFamily="Hero-New" fontSize="sm">
                  {contactTypeLabels[k]}
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
        </FormControl>

        {contactType && contactType !== 'in-person-only' && (
          <Flex gap={4} align="flex-end">
            <FormControl maxW="180px">
              <FormLabel fontSize="xs" fontWeight={600} fontFamily="Hero-New" mb={1}>
                Contact time (minutes)
                {minutesInvalid && (
                  <Text as="span" color="red.500" ml={1}>
                    *
                  </Text>
                )}
              </FormLabel>
              <HStack>
                <NumberInput
                  size="sm"
                  min={1}
                  max={480}
                  bg="white"
                  precision={0}
                  isInvalid={minutesInvalid}
                  value={contactMinutes ?? ''}
                  onChange={v =>
                    form.change(
                      'data.timeWorkLog.preVisitContactMinutes',
                      v === '' ? undefined : Number(v),
                    )
                  }>
                  <NumberInputField
                    _invalid={{ borderColor: 'red.300' }}
                    borderRadius={6}
                    fontFamily="Hero-New"
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="sm" color="gray.500">
                  min
                </Text>
              </HStack>
            </FormControl>
          </Flex>
        )}

        {contactType && contactType !== 'in-person-only' && (
          <FormControl>
            <FormLabel fontSize="xs" fontWeight={600} fontFamily="Hero-New" mb={1}>
              Brief note (optional)
            </FormLabel>
            <Textarea
              size="sm"
              rows={2}
              borderRadius={6}
              fontFamily="Hero-New"
              fontSize="sm"
              placeholder="e.g. Patient called with question about symptoms — advised to come in"
              value={contactNote ?? ''}
              onChange={e =>
                form.change('data.preVisitContact.contactNote', e.target.value || undefined)
              }
            />
          </FormControl>
        )}
      </VStack>
    </Box>
  )
}
